import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { ArrowLeft, Activity, MapPin, Clock, ShieldAlert, AlertTriangle, Radio, FileText } from 'lucide-react';
import { useStore } from '../data/store';
import { TimelineTracker } from '../components/TimelineTracker';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { toast } from 'sonner';
const ALUR_PERINGATAN = ['Terdeteksi', 'Verifikasi', 'Eskalasi', 'Penanganan', 'Selesai'] as const;
export default function AlertDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const alertsList = useStore(s => s.alerts);
  const setAlertStatus = useStore(s => s.setAlertStatus);
  const alert = alertsList.find(a => a.id === id);
  if (!alert) {
    return <div className="flex flex-col items-center justify-center h-full space-y-4" data-id="element-636">
        <h2 className="text-xl font-mono text-muted-foreground" data-id="element-637">
          Peringatan tidak ditemukan
        </h2>
        <Button onClick={() => navigate('/alerts')} variant="outline" data-id="element-638">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-639" /> Kembali ke Daftar
        </Button>
      </div>;
  }
  const isCritical = alert.type === 'KRITIS';
  const isWarning = alert.type === 'WASPADA';
  // Posisi alur penanganan berdasarkan tingkat peringatan.
  const currentAlertStage = isCritical ? 'Eskalasi' : isWarning ? 'Penanganan' : 'Selesai';
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-4xl mx-auto" data-id="element-640">
      <div className="flex items-center gap-4 border-b border-border/50 pb-4" data-id="element-641">
        <Button variant="ghost" size="icon" onClick={() => navigate('/alerts')} className="hover:bg-primary/20 hover:text-primary" data-id="element-642">
          <ArrowLeft className="w-5 h-5" data-id="element-643" />
        </Button>
        <div data-id="element-644">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-645">
            DETAIL PERINGATAN
            <span className={`text-sm font-mono px-2 py-1 rounded border ${isCritical ? 'text-destructive bg-destructive/10 border-destructive/30' : isWarning ? 'text-[var(--status-standby)] bg-[var(--status-standby)]/10 border-[var(--status-standby)]/30' : 'text-primary bg-primary/10 border-primary/30'}`} data-id="element-646">
              {alert.id}
            </span>
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-647">
            Analisis Ancaman Langsung
          </p>
        </div>
      </div>

      <Card className={`glass-panel-heavy shadow-[0_0_30px_rgba(0,0,0,0.5)] ${isCritical ? 'border-destructive/50' : isWarning ? 'border-[var(--status-standby)]/50' : 'border-primary/50'}`} data-id="element-648">
        <CardHeader className={`p-6 border-b border-border/50 ${isCritical ? 'bg-destructive/10' : isWarning ? 'bg-[var(--status-standby)]/10' : 'bg-primary/10'}`} data-id="element-649">
          <div className="flex items-start gap-6" data-id="element-650">
            <div className={`p-4 rounded-full border ${isCritical ? 'bg-destructive/20 border-destructive/50 shadow-[0_0_20px_rgba(var(--destructive),0.3)]' : isWarning ? 'bg-[var(--status-standby)]/20 border-[var(--status-standby)]/50 shadow-[0_0_20px_rgba(var(--status-standby),0.3)]' : 'bg-primary/20 border-primary/50 shadow-[0_0_20px_rgba(var(--primary),0.3)]'}`} data-id="element-651">
              {isCritical ? <ShieldAlert className="w-10 h-10 text-destructive" data-id="element-652" /> : isWarning ? <AlertTriangle className="w-10 h-10 text-[var(--status-standby)]" data-id="element-653" /> : <Radio className="w-10 h-10 text-primary" data-id="element-654" />}
            </div>
            <div className="flex-1" data-id="element-655">
              <div className="mb-3" data-id="element-656">
                <UrgencyBadge urgency={alert.type} className="text-xs px-3 py-1" data-id="element-657" />
              </div>
              <CardTitle className="text-2xl font-heading leading-tight" data-id="element-658">
                {alert.desc}
              </CardTitle>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-8" data-id="element-659">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-660">
            <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-661">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-662">
                <Activity className="w-4 h-4" data-id="element-663" /> Sumber Intelijen
              </span>
              <span className="font-mono text-lg text-primary" data-id="element-664">
                {alert.source}
              </span>
            </div>
            <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-665">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-666">
                <MapPin className="w-4 h-4" data-id="element-667" /> Lokasi Terdeteksi
              </span>
              <span className="font-mono text-lg" data-id="element-668">{alert.location}</span>
            </div>
            <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-669">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-670">
                <Clock className="w-4 h-4" data-id="element-671" /> Waktu Kejadian
              </span>
              <span className="font-mono text-lg" data-id="element-672">{alert.time}</span>
            </div>
          </div>

          <div data-id="element-673">
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-4" data-id="element-674">
              Analisis Konteks
            </span>
            <div className="p-6 bg-black/30 rounded text-base border border-border/30 leading-relaxed text-foreground/90 font-mono" data-id="element-675">
              Peringatan ini dipicu oleh sistem pemantauan otomatis berdasarkan
              pola anomali yang terdeteksi. Evaluasi awal menunjukkan tingkat
              kepercayaan {isCritical ? 'tinggi (85%)' : 'sedang (60%)'}.
              <br data-id="element-676" />
              <br data-id="element-677" />
              Disarankan untuk segera melakukan verifikasi silang dengan sumber
              intelijen lain di wilayah {alert.location} dan menyiagakan
              personel lapangan terdekat.
            </div>
          </div>

          <div data-id="element-678">
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-6" data-id="element-679">
              Alur Penanganan Peringatan
            </span>
            <TimelineTracker stages={ALUR_PERINGATAN} currentStage={currentAlertStage} meta={{
            Terdeteksi: {
              time: alert.time,
              actor: `Sensor ${alert.source}`,
              note: `Anomali terdeteksi otomatis oleh sumber ${alert.source} di ${alert.location}.`
            },
            Verifikasi: {
              time: '+4 mnt',
              actor: 'Operator Command Center',
              note: 'Sinyal diverifikasi silang dengan sumber intelijen lain.'
            },
            Eskalasi: {
              time: isCritical ? 'Berjalan' : '+12 mnt',
              actor: 'Kepala Shift',
              note: 'Peringatan dinaikkan ke tingkat komando untuk penugasan unit.'
            },
            Penanganan: {
              time: isWarning ? 'Berjalan' : '+25 mnt',
              actor: 'Unit Lapangan',
              note: `Tim lapangan ditugaskan menindaklanjuti situasi di ${alert.location}.`
            },
            Selesai: {
              time: alert.type === 'INFO' ? '+40 mnt' : '--:--',
              actor: 'Analis',
              note: 'Situasi tertangani dan peringatan ditutup dengan catatan resmi.'
            }
          }} data-id="element-680" />
          </div>

          <div className="flex gap-4 pt-6 border-t border-border/50" data-id="element-681">
            <Button onClick={() => navigate('/reports')} className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs uppercase tracking-widest py-6" data-id="element-682">
              <FileText className="w-4 h-4 mr-2" data-id="element-683" /> Buat Laporan Resmi
            </Button>
            <Button variant="outline" onClick={() => {
            if (id) {
              setAlertStatus(id, 'Selesai');
              toast.success('Peringatan ditandai selesai.');
            }
          }} className="flex-1 font-mono text-xs uppercase tracking-widest border-border/50 py-6" data-id="element-684">
              Tandai Selesai
            </Button>
            <Button variant="outline" onClick={() => {
            if (id) {
              setAlertStatus(id, 'Diarsipkan');
              toast.success(`${alert.id} ditandai sebagai positif palsu.`);
            }
          }} className="flex-1 font-mono text-xs uppercase tracking-widest border-border/50 py-6" data-id="element-685">
              Tandai Positif Palsu
            </Button>
            <Button variant="outline" onClick={() => {
            if (id) {
              setAlertStatus(id, 'Diarsipkan');
              toast.success(`Peringatan ${alert.id} diabaikan.`);
              navigate('/alerts');
            }
          }} className="flex-1 font-mono text-xs uppercase tracking-widest border-border/50 py-6" data-id="element-686">
              Abaikan
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}